// src/hooks/useNewsPosts.js
import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../utils/api';

// Route through the shared apiFetch helper so this uses the same API base
// (api.piedmontalabamamuseum.com in prod) as the rest of the site.
const NEWS_PATH = '/api/news';

/** Parse 'YYYY-MM-DD' as a LOCAL date */
export function parseYMD(iso) {
    const [y, m, d] = String(iso || '').split('-').map(Number);
    return new Date(y || 1970, (m || 1) - 1, d || 1);
}

export function formatDate(iso) {
    const d = parseYMD(iso);
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
}

export function monthLabel(m) {
    return new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'short' });
}

function mapRowToArticle(row) {
    const photos = Array.isArray(row.photos) ? row.photos : [];
    const images = photos.map((p) => p.url).filter(Boolean);
    const cover =
        row.cover_photo_url ||
        (photos.find((p) => p.is_cover && p.url)?.url) ||
        images[0] ||
        null;

    return {
        id: row.slug || row.id,
        slug: row.slug,
        title: row.title,
        date: row.published_at, // "YYYY-MM-DD"
        summary: row.summary || '',
        body: row.body || '',
        coverUrl: cover,
        images,
    };
}

/**
 * Fetches published news posts from /api/news and returns them as
 * nicely-shaped article objects for the UI.
 *
 * @param {number|undefined} limit optional max rows (passed through to API)
 */
export function useNewsPosts(limit) {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError('');

            try {
                let url = NEWS_PATH;
                if (limit && Number(limit) > 0) {
                    const sep = url.includes('?') ? '&' : '?';
                    url = `${url}${sep}limit=${encodeURIComponent(limit)}`;
                }

                const res = await apiFetch(url, {
                    headers: { Accept: 'application/json' },
                });

                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(data.error || 'Failed to load news.');
                }

                const rows = Array.isArray(data.rows) ? data.rows : [];
                const mapped = rows.map(mapRowToArticle);

                if (!cancelled) {
                    setArticles(mapped);
                }
            } catch (err) {
                console.error('Error loading news posts:', err);
                if (!cancelled) {
                    setError(err.message || 'Failed to load news.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, [limit]);

    const sorted = useMemo(
        () => articles.slice().sort((a, b) => parseYMD(b.date) - parseYMD(a.date)),
        [articles]
    );

    return { articles: sorted, loading, error };
}
